import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_MY_CASES } from '../graphql/clientQueries';
import { GET_USER } from '../../graphql/queries';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import type { Case, CaseStatus } from '../types';

/**
 * Status configuration for case status badges
 */
const STATUS_CONFIG: Record<CaseStatus, { label: string; color: string; bgColor: string }> = {
  INQUIRY: { label: 'Inquiry', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  OPEN: { label: 'Open', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  IN_PROGRESS: { label: 'In Progress', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  CLOSED: { label: 'Closed', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  NEW: { label: 'New', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  NEGOTIATION: { label: 'Negotiation', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  LITIGATION: { label: 'Litigation', color: 'text-red-700', bgColor: 'bg-red-100' },
  SETTLED: { label: 'Settled', color: 'text-green-700', bgColor: 'bg-green-100' },
};

interface GetMyCasesResponse {
  listMyCases: Case[];
}

/**
 * CaseScreen - Client case list and details view
 * 
 * Displays case information including:
 * - List of all cases with status badges
 * - Expandable detail view for each case
 * - Assigned attorney and case manager with contact actions
 * - Pull-to-refresh functionality
 */
export function CaseScreen() {
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetMyCasesResponse>(GET_MY_CASES, {
    fetchPolicy: 'cache-and-network',
  });

  // Filter out INQUIRY status cases - they're still in the inquiry phase
  const allCases = data?.listMyCases ?? [];
  const cases = allCases.filter(c => c.status !== 'INQUIRY');
  const selectedCase = selectedCaseId 
    ? cases.find(c => c.id === selectedCaseId) 
    : cases[0];

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatShortDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status as CaseStatus] || { 
      label: status, 
      color: 'text-gray-700', 
      bgColor: 'bg-gray-100' 
    };
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-aalaw-red mb-4"></div>
        <p className="text-gray-500">Loading your cases...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-4">😕</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load cases</h2>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  // Empty state - no cases
  if (cases.length === 0) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-4">📋</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Cases Yet</h2>
        <p className="text-gray-600 text-center mb-4">
          Your case information will appear here once your inquiry has been accepted.
        </p>
        <p className="text-aalaw-red font-medium">
          Questions? Contact us at (555) 123-4567
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Cases</h1>
            <p className="text-sm text-gray-500">{cases.length} {cases.length === 1 ? 'case' : 'cases'}</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <span className="animate-spin mr-2">↻</span>
            ) : (
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
        {/* Case List - Left Panel */}
        <div className={`${cases.length > 1 ? 'lg:w-1/3 lg:border-r border-gray-200' : 'hidden'} overflow-y-auto`}>
          <div className="p-4 space-y-3">
            {cases.map((caseItem) => {
              const statusConfig = getStatusConfig(caseItem.status);
              const isSelected = selectedCase?.id === caseItem.id;
              
              return (
                <button
                  key={caseItem.id}
                  onClick={() => setSelectedCaseId(caseItem.id)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    isSelected 
                      ? 'border-aalaw-red bg-red-50 shadow-sm' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">{caseItem.caseNumber}</h3>
                      <p className="text-xs text-gray-600">{caseItem.caseType}</p>
                    </div>
                    <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0 text-xs`}>
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500">
                    Opened {formatShortDate(caseItem.createdAt)}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Case Detail - Right Panel */}
        <div className={`flex-1 overflow-y-auto p-4 sm:p-6 overscroll-contain scrollbar-visible ${cases.length === 1 ? 'w-full' : ''}`} style={{ WebkitOverflowScrolling: 'touch' }}>
          {selectedCase ? (
            <CaseDetail 
              caseData={selectedCase} 
              formatDate={formatDate} 
              getStatusConfig={getStatusConfig} 
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              Select a case to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * CaseDetail - Displays detailed information for a single case
 */
function CaseDetail({ 
  caseData, 
  formatDate, 
  getStatusConfig 
}: { 
  caseData: Case; 
  formatDate: (date?: string) => string;
  getStatusConfig: (status: string) => { label: string; color: string; bgColor: string };
}) {
  const statusConfig = getStatusConfig(caseData.status);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Case Overview Card */}
        <Card className="lg:col-span-2">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Case Overview</h2>
              <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0`}>
                {statusConfig.label}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-500">Case Number</span>
                <span className="font-medium text-gray-900">{caseData.caseNumber}</span>
              </div>

              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-500">Case Type</span>
                <span className="font-medium text-gray-900">{caseData.caseType}</span>
              </div>

              {caseData.incidentDate && (
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-500">Incident Date</span>
                  <span className="font-medium text-gray-900">{formatDate(caseData.incidentDate)}</span>
                </div>
              )}

              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-500">Case Opened</span>
                <span className="font-medium text-gray-900">{formatDate(caseData.createdAt)}</span>
              </div>
            </div>

            {caseData.description && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <span className="text-gray-500 block mb-2">Description</span>
                <p className="text-gray-900 leading-relaxed">{caseData.description}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Legal Team Card */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Your Legal Team</h2>

            <div className="space-y-4">
              {/* Attorney */}
              {caseData.assignedAttorneyId ? (
                <AssignedPersonCard 
                  userId={caseData.assignedAttorneyId} 
                  role="Attorney" 
                />
              ) : (
                <div className="flex flex-col items-center py-4 text-center border-b border-gray-100">
                  <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <p className="text-gray-500 italic text-sm">Attorney not yet assigned</p>
                </div>
              )}

              {/* Case Manager */}
              {caseData.assignedCaseManagerId ? (
                <AssignedPersonCard 
                  userId={caseData.assignedCaseManagerId} 
                  role="Case Manager" 
                />
              ) : (
                <div className="flex flex-col items-center py-4 text-center">
                  <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <p className="text-gray-500 italic text-sm">Case Manager not yet assigned</p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* What's Next Card */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">What's Next?</h2>

            <div className="flex flex-col items-center py-4 text-center">
              <svg className="w-10 h-10 text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600 leading-relaxed">
                Your case is being actively worked on. You can message your legal team, 
                upload documents, and check your tasks for any action items.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}


/**
 * AssignedPersonCard - Fetches and displays assigned staff member with contact actions
 */
function AssignedPersonCard({ userId, role }: { userId: string; role: string }) {
  const { data, loading } = useQuery<{ getUser: { id: string; firstName: string; lastName: string; email: string; phoneNumber?: string; role: string } }>(GET_USER, {
    variables: { id: userId },
    skip: !userId,
  });

  if (loading) {
    return (
      <div className="flex items-center gap-4 py-4 border-b border-gray-100 last:border-b-0">
        <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-20 mb-2 animate-pulse"></div>
          <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
      </div>
    );
  }

  const user = data?.getUser;

  if (!user) {
    return (
      <div className="text-gray-500 italic py-4">
        {role} information unavailable
      </div>
    );
  }

  const fullName = user.firstName && user.lastName 
    ? `${user.firstName} ${user.lastName}`
    : user.email;

  const initials = user.firstName 
    ? user.firstName[0].toUpperCase()
    : user.email[0].toUpperCase();

  const handleCall = () => {
    if (user.phoneNumber) {
      window.location.href = `tel:${user.phoneNumber}`;
    }
  };

  const handleEmail = () => {
    if (user.email) {
      window.location.href = `mailto:${user.email}`;
    }
  };

  return (
    <div className="flex items-center gap-4 py-4 border-b border-gray-100 last:border-b-0">
      {/* Avatar */}
      <div className="w-14 h-14 rounded-full bg-aalaw-red flex items-center justify-center flex-shrink-0 shadow-md">
        <span className="text-xl font-bold text-white">{initials}</span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-500">{role}</p>
        <p className="font-semibold text-gray-900 truncate">{fullName}</p>
        {user.email && (
          <p className="text-sm text-gray-500 truncate">{user.email}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {user.phoneNumber && (
          <button
            onClick={handleCall}
            className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            title={`Call ${fullName}`}
            aria-label={`Call ${fullName}`}
          >
            <span className="text-lg">📞</span>
          </button>
        )}
        <button
          onClick={handleEmail}
          className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          title={`Email ${fullName}`}
          aria-label={`Email ${fullName}`}
        >
          <span className="text-lg">✉️</span>
        </button>
      </div>
    </div>
  );
}
